import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useHaptic } from "@/hooks/useHaptic";
import { supabase } from "@/integrations/supabase/client";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import {
  Sun,
  CloudSun,
  Cloud,
  CloudRain,
  CloudSnow,
  Upload,
  X,
  Plus,
  Users,
  Calendar,
  FileText,
  Image,
  Video,
  AlertTriangle,
  Send,
  Sparkles,
  Target,
  Mic
} from "lucide-react";

interface DiarioObrasModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onSuccess: () => void;
  editingEntry?: any; // Entrada para edição (opcional)
  initialFiles?: File[];
}

const weatherOptions = [
  { value: "ensolarado", label: "Ensolarado", icon: Sun, color: "text-yellow-500" },
  { value: "parcialmente_nublado", label: "Parcialmente Nublado", icon: CloudSun, color: "text-blue-400" },
  { value: "nublado", label: "Nublado", icon: Cloud, color: "text-gray-500" },
  { value: "chuvoso", label: "Chuvoso", icon: CloudRain, color: "text-blue-600" },
  { value: "tempestade", label: "Tempestade", icon: CloudSnow, color: "text-purple-600" },
];

const suggestedTags = [
  "Concreto", "Hidráulica", "Pintura", "Atraso", "Limpeza",
  "Instalação elétrica", "Alvenaria", "Fundação", "Estrutura",
  "Acabamento", "Impermeabilização", "Telhado"
];

export const DiarioObrasModal = ({ isOpen, onClose, projectId, onSuccess, editingEntry, initialFiles }: DiarioObrasModalProps) => {
  const { toast } = useToast();
  const haptic = useHaptic();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    clima: "",
    equipe: "",
    atividades: "",
    tags: [] as string[],
    ocorrencia_critica: false,
    resumo_cliente: "",
  });

  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTag, setNewTag] = useState("");

  // Carregar dados da entrada quando estiver editando
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        data: editingEntry.data,
        clima: editingEntry.clima,
        equipe: editingEntry.equipe.toString(),
        atividades: editingEntry.atividades,
        tags: editingEntry.tags || [],
        ocorrencia_critica: editingEntry.ocorrencia_critica,
        resumo_cliente: editingEntry.resumo_cliente || "",
      });
      setFiles([]); // Reset files for editing
    } else {
      // Reset form for new entry
      setFormData({
        data: new Date().toISOString().split('T')[0],
        clima: "",
        equipe: "",
        atividades: "",
        tags: [],
        ocorrencia_critica: false,
        resumo_cliente: "",
      });
      setFiles([]);
    }
  }, [editingEntry]);

  // Reset form when modal closes
  // Reset form when modal closes or opens
  useEffect(() => {
    if (!isOpen) {
      setUploadProgress(0);
      setNewTag("");
      // Don't clear files here if we want to preserve initialFiles logic re-running on open, 
      // but usually we want clean slate on close. 
      // If we rely on initialFiles prop change, we should handle it below.
    }

    if (isOpen && initialFiles && initialFiles.length > 0 && !editingEntry) {
      setFiles(initialFiles);
    }
  }, [isOpen, initialFiles, editingEntry]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = selectedFiles.filter(file => {
      const isValid = file.size <= 50 * 1024 * 1024; // 50MB limit
      if (!isValid) {
        toast({
          title: "Arquivo muito grande",
          description: `${file.name} excede o limite de 50MB`,
          variant: "destructive",
        });
      }
      return isValid;
    });

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const addTag = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
    setNewTag("");
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const uploadFiles = async (entryId: string): Promise<string[]> => {
    if (files.length === 0) return [];

    // Obter o user_id atual
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    const uploadedUrls: string[] = [];
    const totalFiles = files.length;

    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];
      const fileName = `${Date.now()}-${file.name}`;
      const filePath = `${user.id}/${projectId}/${entryId}/${fileName}`;

      try {
        const { data, error } = await supabase.storage
          .from('diario-arquivos')
          .upload(filePath, file);

        if (error) throw error;

        // Obter URL pública do arquivo
        const { data: { publicUrl } } = supabase.storage
          .from('diario-arquivos')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      } catch (error) {
        console.error('Erro no upload:', error);
        toast({
          title: "Erro no upload",
          description: `Falha ao enviar ${file.name}`,
          variant: "destructive",
        });
      }
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clima || !formData.equipe || !formData.atividades) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha clima, equipe e atividades",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      let entryData;
      let uploadedUrls: string[] = [];

      if (editingEntry) {
        // Modo edição - atualizar entrada existente
        const { data: updatedEntry, error: updateError } = await supabase
          .from('diario_obras')
          .update({
            data: formData.data,
            clima: formData.clima,
            equipe: parseInt(formData.equipe),
            atividades: formData.atividades,
            tags: formData.tags,
            ocorrencia_critica: formData.ocorrencia_critica,
            resumo_cliente: formData.resumo_cliente || null,
          })
          .eq('id', editingEntry.id)
          .select()
          .single();

        if (updateError) throw updateError;
        entryData = updatedEntry;

        // Upload de novos arquivos se houver
        if (files.length > 0) {
          uploadedUrls = await uploadFiles(editingEntry.id);

          // Manter arquivos existentes e adicionar novos
          const currentFiles = editingEntry.arquivos || [];
          const allFiles = [...currentFiles, ...uploadedUrls];

          const { error: filesUpdateError } = await supabase
            .from('diario_obras')
            .update({ arquivos: allFiles })
            .eq('id', editingEntry.id);

          if (filesUpdateError) throw filesUpdateError;
        }

        toast({
          title: "Entrada atualizada com sucesso! ✨",
          description: "As alterações foram salvas no diário de obras.",
        });
      } else {
        // Modo criação - criar nova entrada
        const { data: newEntry, error: insertError } = await supabase
          .from('diario_obras')
          .insert({
            projeto_id: projectId,
            data: formData.data,
            clima: formData.clima,
            equipe: parseInt(formData.equipe),
            atividades: formData.atividades,
            tags: formData.tags,
            ocorrencia_critica: formData.ocorrencia_critica,
            resumo_cliente: formData.resumo_cliente || null,
            user_id: (await supabase.auth.getUser()).data.user?.id,
            arquivos: [] // Será atualizado após upload
          })
          .select()
          .single();

        if (insertError) throw insertError;
        entryData = newEntry;

        // Upload de arquivos se houver
        if (files.length > 0) {
          uploadedUrls = await uploadFiles(entryData.id);

          const { error: updateError } = await supabase
            .from('diario_obras')
            .update({ arquivos: uploadedUrls })
            .eq('id', entryData.id);

          if (updateError) throw updateError;
        }

        toast({
          title: "Entrada criada com sucesso! 🎉",
          description: "Sua entrada no diário foi salva e já está disponível na timeline.",
        });
      }

      // Resetar formulário apenas se não estiver editando
      if (!editingEntry) {
        setFormData({
          data: new Date().toISOString().split('T')[0],
          clima: "",
          equipe: "",
          atividades: "",
          tags: [],
          ocorrencia_critica: false,
          resumo_cliente: "",
        });
      }
      setFiles([]);
      setUploadProgress(0);

      onSuccess();
      onClose();

    } catch (error) {
      console.error('Erro ao salvar entrada:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar a entrada. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedWeather = weatherOptions.find(w => w.value === formData.clima);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            {editingEntry ? 'Editar Entrada do Diário' : 'Nova Entrada no Diário'}
            <Sparkles className="h-5 w-5 text-yellow-500" />
          </DialogTitle>
          <DialogDescription>
            Registre as atividades executadas no canteiro de obras, clima do dia, equipe presente e anexe arquivos importantes.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Voice Recorder - Diário por Voz */}
          {!editingEntry && (
            <VoiceRecorder
              onTranscriptionComplete={(result) => {
                // Apply transcribed and structured data to form
                if (result.text) {
                  setFormData(prev => ({
                    ...prev,
                    atividades: result.structured?.atividades || result.text,
                    clima: result.structured?.clima || prev.clima,
                    equipe: result.structured?.equipe?.toString() || prev.equipe,
                    tags: result.structured?.tags || prev.tags,
                    ocorrencia_critica: result.structured?.ocorrencia_critica ?? prev.ocorrencia_critica,
                    resumo_cliente: result.structured?.resumo_cliente || prev.resumo_cliente,
                  }));
                  toast({
                    title: "Áudio transcrito! 🎙️",
                    description: "Os dados foram preenchidos automaticamente. Revise e ajuste se necessário.",
                  });
                }
              }}
            />
          )}

          {/* Barra de Progresso Visual */}
          <div className="p-4 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progresso do Formulário</span>
              <Target className="h-4 w-4 text-primary" />
            </div>
            <Progress
              value={
                (formData.clima ? 25 : 0) +
                (formData.equipe ? 25 : 0) +
                (formData.atividades ? 25 : 0) +
                (formData.tags.length > 0 ? 25 : 0)
              }
              className="h-2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Data da Entrada */}
            <div className="space-y-2">
              <Label htmlFor="data" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Data da Entrada
              </Label>
              <Input
                id="data"
                type="date"
                value={formData.data}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                className="font-medium"
              />
            </div>

            {/* Equipe no Canteiro */}
            <div className="space-y-2">
              <Label htmlFor="equipe" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Equipe no Canteiro
              </Label>
              <Input
                id="equipe"
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                min="1"
                enterKeyHint="next"
                placeholder="Quantas pessoas estavam trabalhando hoje?"
                value={formData.equipe}
                onChange={(e) => setFormData(prev => ({ ...prev, equipe: e.target.value }))}
                className="font-medium min-h-[44px]"
              />
            </div>
          </div>

          {/* Clima do Dia */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              {selectedWeather && <selectedWeather.icon className={`h-4 w-4 ${selectedWeather.color}`} />}
              Clima do Dia
            </Label>
            <Select value={formData.clima} onValueChange={(value) => setFormData(prev => ({ ...prev, clima: value }))}>
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Selecione o clima do dia" />
              </SelectTrigger>
              <SelectContent>
                {weatherOptions.map((weather) => (
                  <SelectItem key={weather.value} value={weather.value}>
                    <div className="flex items-center gap-2">
                      <weather.icon className={`h-4 w-4 ${weather.color}`} />
                      {weather.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Atividades Executadas */}
          <div className="space-y-2">
            <Label htmlFor="atividades" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Atividades Executadas
            </Label>
            <Textarea
              id="atividades"
              placeholder="Descreva detalhadamente o que foi realizado no dia..."
              value={formData.atividades}
              onChange={(e) => setFormData(prev => ({ ...prev, atividades: e.target.value }))}
              maxLength={500}
              rows={4}
              className="resize-none"
            />
            <div className="text-xs text-muted-foreground text-right">
              {formData.atividades.length}/500 caracteres
            </div>
          </div>

          {/* Tags/Palavras-chave */}
          <div className="space-y-3">
            <Label>Tags/Palavras-chave</Label>

            {/* Tags selecionadas */}
            <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-3 border rounded-lg bg-muted/30">
              {formData.tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1">
                  {tag}
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-red-500"
                    onClick={() => removeTag(tag)}
                  />
                </Badge>
              ))}
              {formData.tags.length === 0 && (
                <span className="text-muted-foreground text-sm">Nenhuma tag selecionada</span>
              )}
            </div>

            {/* Campo para nova tag */}
            <div className="flex gap-2">
              <Input
                placeholder="Digite uma nova tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag(newTag))}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => addTag(newTag)}
                disabled={!newTag}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Tags sugeridas */}
            <div className="flex flex-wrap gap-2">
              {suggestedTags.filter(tag => !formData.tags.includes(tag)).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                  onClick={() => addTag(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Ocorrência Crítica */}
          <div className="flex items-center space-x-3 p-4 border rounded-lg bg-red-50/50 dark:bg-red-950/20">
            <Checkbox
              id="ocorrencia"
              checked={formData.ocorrencia_critica}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, ocorrencia_critica: !!checked }))}
            />
            <Label htmlFor="ocorrencia" className="flex items-center gap-2 cursor-pointer">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Marcar como Ocorrência Crítica
            </Label>
          </div>

          {/* Upload de Arquivos */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Arquivos (Imagens, Vídeos, Documentos)
            </Label>

            <div
              className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Clique aqui ou arraste arquivos para fazer upload
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Máximo 50MB por arquivo
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx"
              onChange={handleFileUpload}
              className="hidden"
            />

            {/* Lista de arquivos */}
            {files.length > 0 && (
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <div className="flex items-center gap-2">
                      {file.type.startsWith('image/') ? (
                        <Image className="h-4 w-4" />
                      ) : file.type.startsWith('video/') ? (
                        <Video className="h-4 w-4" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                      <span className="text-sm truncate">{file.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resumo para Cliente */}
          <div className="space-y-2">
            <Label htmlFor="resumo" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Resumo para Cliente (Opcional)
            </Label>
            <Textarea
              id="resumo"
              placeholder="Resumo simples e objetivo para mostrar ao cliente..."
              value={formData.resumo_cliente}
              onChange={(e) => setFormData(prev => ({ ...prev, resumo_cliente: e.target.value }))}
              rows={3}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Este resumo será usado futuramente pela IA para gerar relatórios automáticos
            </p>
          </div>

          {/* Upload Progress */}
          {isSubmitting && uploadProgress > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Enviando arquivos...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                haptic.tap();
                onClose();
              }}
              disabled={isSubmitting}
              className="flex-1 min-h-[44px]"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.clima || !formData.equipe || !formData.atividades}
              className="flex-1 min-h-[44px] bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
              onClick={() => haptic.impact()}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {editingEntry ? 'Salvar Alterações' : 'Salvar Entrada'}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};